import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { erpAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import { rows, errorText, date, variantDisplay, loc, DataTable, Stat, Loading } from '../../utils/erpHelpers';
import '../../components/admin/AdminFormStyles.css';
import './ERPManagement.css';

const InventoryTransfers: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<Record<string, any[]>>({});
  const [me, setMe] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const closeToast = useCallback(() => setMessage(null), []);
  const [locationFilter, setLocationFilter] = useState(() => sessionStorage.getItem('erp-location-filter') || '');

  const can = useCallback((code: string) => Boolean(me?.is_superuser || me?.is_admin || (me?.permissions || []).includes(code)), [me]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const paramsFor = (resource: string) => {
        const params: any = {};
        if (locationFilter && resource !== 'inventory') params.location = locationFilter;
        return Object.keys(params).length ? params : undefined;
      };
      const resources = ['locations', 'transfers'];
      const [identity, ...responses] = await Promise.all([erpAPI.me(), ...resources.map(r => erpAPI.list(r, paramsFor(r)))]);
      setMe(identity.data);
      const next: Record<string, any[]> = {};
      resources.forEach((name, i) => { next[name] = rows(responses[i].data); });
      setData(next);
      const available = next.locations || [];
      if (!locationFilter && available.length === 1) setLocationFilter(String(available[0].id));
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
    finally { setLoading(false); }
  }, [locationFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const change = (event: Event) => {
      const location = String((event as CustomEvent).detail?.location || '');
      sessionStorage.setItem('erp-location-filter', location);
      setLocationFilter(location);
    };
    window.addEventListener('erp-location-change', change);
    return () => window.removeEventListener('erp-location-change', change);
  }, []);
  useEffect(() => {
    const fn = () => load(); window.addEventListener('admin-refresh', fn);
    return () => window.removeEventListener('admin-refresh', fn);
  }, [load]);

  const run = async (work: () => Promise<any>, success: string) => {
    try {
      await work();
      setMessage({ type: 'success', text: success });
      await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const locations = data.locations || [];

  if (loading && !Object.keys(data).length) return <Loading />;

  return (
    <div className="erp-page">
      <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={closeToast} duration={message?.type === 'error' ? 5000 : 3500} />

      <div className="erp-workspace-toolbar">
        <div>
          <strong>Manage Stocks</strong>
          <p>Review, approve, dispatch, and receive stock transfers. Create a new transfer from the button above.</p>
        </div>
        {can('transfer.create') && <button className="erp-btn" onClick={() => navigate('/admin/inventory/transfers/create')}><Plus size={15} />New stock transfer</button>}
      </div>

      <div className="erp-stats">
        <Stat label="Pending approval" value={(data.transfers || []).filter(x => x.status === 'APPROVAL_PENDING').length} />
        <Stat label="Approved" value={(data.transfers || []).filter(x => x.status === 'APPROVED').length} />
        <Stat label="In transit" value={(data.transfers || []).filter(x => x.status === 'DISPATCHED').length} />
        <Stat label="Received" value={(data.transfers || []).filter(x => x.status === 'RECEIVED').length} />
      </div>

      <DataTable title="Stock transfers" columns={['Number', 'Purpose', 'From → To', 'Items', 'Status', 'Responsible users', 'Timeline', 'Action']}
        rows={(data.transfers || []).map(x => [
          x.transfer_number,
          String(x.purpose || '').replaceAll('_', ' '),
          <><strong>{loc(locations, x.source)}</strong><br /><small>to {loc(locations, x.destination)}</small></>,
          <>{x.items?.length || 0} variant(s)<br /><small>{(x.items || []).map((item: any) => `${variantDisplay(item)} × ${item.quantity}`).join(', ')}</small></>,
          <span className={`erp-status ${String(x.status).toLowerCase()}`}>{String(x.status).replaceAll('_', ' ')}</span>,
          <small>Created: {x.created_by_name || `User #${x.created_by}`}<br />Approved: {x.approved_by_name || '—'}<br />Dispatched: {x.dispatched_by_name || '—'}<br />Received: {x.received_by_name || '—'}</small>,
          <small>Created {date(x.created_at)}{x.approved_at && <><br />Approved {date(x.approved_at)}</>}{x.dispatched_at && <><br />Dispatched {date(x.dispatched_at)}</>}{x.received_at && <><br />Received {date(x.received_at)}</>}</small>,
          x.status === 'APPROVAL_PENDING' ?
            <div className="erp-actions">
              {can('transfer.approve') && <button className="erp-btn" onClick={() => run(() => erpAPI.action('transfers', x.id, 'approve'), 'Transfer approved.')}>Approve</button>}
              {can('transfer.create') && <button className="erp-btn danger" onClick={() => run(() => erpAPI.action('transfers', x.id, 'cancel'), 'Transfer cancelled.')}>Cancel</button>}
            </div> :
            x.status === 'APPROVED' ?
              <div className="erp-actions">
                {(can('transfer.create') && (me?.is_admin || Number(me?.user_id) === Number(x.created_by))) && <button className="erp-btn" onClick={() => run(() => erpAPI.action('transfers', x.id, 'dispatch'), 'Transfer dispatched; source stock updated.')}>Dispatch</button>}
                {can('transfer.create') && <button className="erp-btn danger" onClick={() => run(() => erpAPI.action('transfers', x.id, 'cancel'), 'Transfer cancelled.')}>Cancel</button>}
              </div> :
              x.status === 'DISPATCHED' && can('inventory.change') ?
                <button className="erp-btn" onClick={() => run(() => erpAPI.action('transfers', x.id, 'receive'), 'Transfer received; destination stock updated.')}>Receive</button> :
                null
        ])} />
    </div>
  );
};

export default InventoryTransfers;